import type { RouteLocationRaw } from 'vue-router';

import { fetchStarters, copyStarterIntoCampaign } from '@/campaigns/starters';
import logger from '@/app/logger';

// TODO: type this — Vuex store
/* eslint-disable @typescript-eslint/no-explicit-any */
type Store = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

export const TUTORIAL_STARTER = 'tutorial';

const TUTORIAL_CAMPAIGN = {
    name: 'Tutorial',
    description: 'A campaign designed to get you familiar with the features of Mythic Table',
    imageUrl: '/static/assets/tutorial/campaign-banner.jpg',
    tutorialCampaign: true,
};

/**
 * Builds a first-time player their tutorial campaign from the published starter and returns its id,
 * so the caller can take them straight into it. Returns null when there is nothing to do.
 *
 * The flag is cleared before any of the work. A player who hits a failure ends up without a tutorial,
 * which the starter picker can still give them, rather than collecting a fresh one on every login.
 */
export async function buildTutorialCampaign(store: Store): Promise<string | null> {
    const me = store.state.profile.me;
    if (!me?.needsTutorial) {
        return null;
    }

    await store.dispatch('profile/update', { ...me, needsTutorial: false });

    try {
        const starter = (await fetchStarters()).find((candidate) => candidate.name === TUTORIAL_STARTER);
        if (!starter) {
            logger.warn(`No "${TUTORIAL_STARTER}" starter is published, so this player gets no tutorial.`);
            return null;
        }

        const campaign = await store.dispatch('campaigns/createCampaign', { ...TUTORIAL_CAMPAIGN });
        if (!campaign?.id) {
            logger.warn('Creating the tutorial campaign returned no id.');
            return null;
        }

        await copyStarterIntoCampaign(starter, campaign.id);
        return campaign.id;
    } catch (error) {
        logger.warn('Could not build the tutorial campaign', error);
        return null;
    }
}

/**
 * Route guard for the campaigns scene. Reaching it is what earns a first-time player their
 * tutorial; arriving anywhere else — an invitation, a shared library link — is an errand they
 * came here to run, and the flag survives to be spent on their next visit.
 *
 * Redirecting from here rather than from the component means the campaign list is never mounted
 * for a player who is only passing through it.
 */
export async function enterCampaignsScene(store: Store): Promise<true | RouteLocationRaw> {
    try {
        await store.dispatch('profile/me');
        const campaignId = await buildTutorialCampaign(store);
        if (campaignId) {
            return { name: 'live-play', params: { sessionId: campaignId } };
        }
    } catch (error) {
        logger.warn('Could not decide on the tutorial, so the campaign list stands', error);
    }
    return true;
}
